import { generateApiActionTypes } from 'utils/actions/generateActionTypes'
import { ENDPOINTS } from 'utils/api/callApi'
import { SHIPPER_RATE_STATUS } from 'apps/rms/constants/shipperRates'
import {
  IShipperRatesParams,
  IShipperRate,
  IBulkUploadFormState,
} from 'apps/rms/types/shipperRates'
import { keymirror } from 'utils/keymirror'

const SHIPPER_RATES_KEY = 'SHIPPER_RATES'
export const SHIPPER_RATES_ACTIONS = generateApiActionTypes(SHIPPER_RATES_KEY)

export const fetchShipperRates = (params: IShipperRatesParams) => {
  const { filters, pageCount, perPage, sortField, sortOrder } = params
  const {
    activeStatus,
    awardType,
    deliveryCity,
    deliveryPostalCode,
    deliveryStateCode,
    equipmentId,
    opsRepId,
    pickupCity,
    pickupPostalCode,
    pickupStateCode,
    pricingProgram,
    shipperApplicationId,
    shipperLaneId,
    shipperRateId,
  } = filters || {}

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.shipperRates,
        method: 'GET',
        params: {
          pageCount,
          perPage,
          sortField,
          sortOrder,
          filters: {
            pickupCity,
            pickupStateCode,
            pickupPostalCode,
            deliveryCity,
            deliveryStateCode,
            deliveryPostalCode,
            shipperApplicationId,
            shipperLaneId,
            shipperRateId,
            awardType: awardType || undefined,
            equipmentId: equipmentId || undefined,
            pricingProgram: pricingProgram || undefined,
            opsRepId,
            activeStatus,
          },
        },
      },
      actionTypes: Object.keys(SHIPPER_RATES_ACTIONS),
    },
    type: '',
  }
}

const shipperRatePayload = (shipperRate: RecursivePartial<IShipperRate>) => {
  const {
    acceptanceCap,
    acceptanceCapCadence,
    awardType,
    delivery,
    endDate,
    equipment,
    existingAwardId,
    isDeliveryDropTrailer,
    isPickupDropTrailer,
    isRepricedRate,
    minChargeRate,
    pickup,
    pricingType,
    rate,
    shipperApplication,
    shipperLaneId,
    shipperMiles,
    startDate,
    volume,
    volumeType,
  } = shipperRate
  const { id: shipperApplicationId } = shipperApplication || {}
  const { key: equipmentKey } = equipment || {}
  const { amount: rateAmount, rateTypeKey: rateType } = rate || {}
  const { amount: minChargeAmount, rateTypeKey: minChargeKey } = minChargeRate || {}
  const {
    city: pickupCity,
    isoCountryCode: pickupIsoCountryCode,
    postalCode: pickupPostalCode,
    stateCode: pickupStateCode,
  } = pickup || {}
  const {
    city: deliveryCity,
    isoCountryCode: deliveryIsoCountryCode,
    postalCode: deliveryPostalCode,
    stateCode: deliveryStateCode,
  } = delivery || {}

  const rateAttributes = rate ? { amount: rateAmount, rateTypeKey: rateType } : null
  const minChargeRateAttributes = minChargeRate
    ? { amount: minChargeAmount, rateTypeKey: minChargeKey }
    : null

  const payload = {
    shipperLaneId,
    shipperApplicationId,
    startDate,
    endDate,
    rateAttributes,
    minChargeRateAttributes,
    equipmentKey,
    awardType,
    volume,
    volumeType,
    pickupCity,
    pickupStateCode,
    pickupPostalCode,
    pickupIsoCountryCode,
    deliveryCity,
    deliveryStateCode,
    deliveryPostalCode,
    deliveryIsoCountryCode,
    isPickupDropTrailer,
    isDeliveryDropTrailer,
    shipperMiles,
    isRepricedRate,
    acceptanceCap,
    acceptanceCapCadence,
    pricingType,
    existingAwardId,
  }

  return payload
}

export const CREATE_SHIPPER_RATES_ACTIONS = generateApiActionTypes('CREATE_SHIPPER_RATES')
export const createShipperRate = (params: RecursivePartial<IShipperRate>) => {
  const payload = shipperRatePayload(params)

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.shipperRates,
        method: 'POST',
        body: payload,
      },
      actionTypes: Object.keys(CREATE_SHIPPER_RATES_ACTIONS),
    },
    type: '',
  }
}

export const BULK_CREATE_SHIPPER_RATES_ACTIONS = generateApiActionTypes('BULK_CREATE_SHIPPER_RATES')
export const bulkCreateShipperRate = (formState: IBulkUploadFormState) => {
  const { email, file } = formState || {}
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.shipperRates,
        group: 'bulk_create',
        method: 'POST',
        body: {
          bulkTemplate: file,
          email,
        },
      },
      actionTypes: Object.keys(BULK_CREATE_SHIPPER_RATES_ACTIONS),
    },
    type: '',
  }
}

export const UPDATE_SHIPPER_RATES_ACTIONS = generateApiActionTypes('UPDATE_SHIPPER_RATES')
export const updateShipperRate = (params: RecursivePartial<IShipperRate>) => {
  const payload = shipperRatePayload(params)

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.shipperRates,
        method: 'PUT',
        id: params.id,
        body: payload,
      },
      actionTypes: Object.keys(UPDATE_SHIPPER_RATES_ACTIONS),
    },
    type: '',
  }
}

export const BULK_UPDATE_SHIPPER_RATES_ACTIONS = generateApiActionTypes('BULK_UPDATE_SHIPPER_RATES')
export const bulkUpdateShipperRate = (formState: IBulkUploadFormState) => {
  const { email, file } = formState || {}
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: ENDPOINTS.shipperRates,
        group: 'bulk_update',
        method: 'POST',
        body: {
          bulkTemplate: file,
          email,
        },
      },
      actionTypes: Object.keys(BULK_UPDATE_SHIPPER_RATES_ACTIONS),
    },
    type: '',
  }
}

export const SET_ACTIVE_TAB = 'SET_ACTIVE_TAB'
export const setActiveTab = (tab: SHIPPER_RATE_STATUS) => ({
  type: SET_ACTIVE_TAB,
  payload: { tab },
})

export const EDIT_SHIPPER_RATE_MODAL = keymirror({
  EDIT_SHIPPER_RATE_MODAL_OPEN: undefined,
  EDIT_SHIPPER_RATE_MODAL_CLOSE: undefined,
})

export const openEditShipperRateModal = (rate?: IShipperRate) => ({
  type: EDIT_SHIPPER_RATE_MODAL.EDIT_SHIPPER_RATE_MODAL_OPEN,
  payload: { rate },
})

export const closeEditShipperRateModal = () => ({
  type: EDIT_SHIPPER_RATE_MODAL.EDIT_SHIPPER_RATE_MODAL_CLOSE,
})
